import { httpRequest } from '../../shared/lib/utils/httpClient';
import type { DynamicForm } from '@dynamicForm/FieldConfig';
import { type GeneralType } from './caseManagerServices';
import { type CancelReasonFacilityParams } from '../../pages/dashboard/CancelConsultConfirmationPopup';
import { PORTAL_TYPES } from '@qler-view/shared_lib/src';

export interface FacilityList {
  id: number;
  name: string;
}

export interface CreateStatusList {
  id: number;
  name: string;
}

export interface ConsultsMetaData {
  facilityList: FacilityList[];
  createStatusList: CreateStatusList[];
}

export interface SessionFields {
  patientFirstName: string;
  patientLastName: string;
  dateOfBirth?: string;
  patientMrn: string;
  callBackNumber: string;
  reasonForConsult: string;
  unitOrDepartment: string;
  referringProvider: string;
  cart: string;
}

export interface AdditionalSessionFields {
  id: number;
  fieldName: string;
  fieldValue: string | number;
}

export interface ConsultForm {
  id: number;
  facilityId: number;
  sessionStatusId: number;
  sessionId: string;
  consultAssignedToProviderId: number;
  sessionFields: SessionFields;
  additionalSessionFields: AdditionalSessionFields[];
}

export interface CancelConsultMetadataResponse {
  cancelReasonList: GeneralType[];
  cancelTechIssueList: GeneralType[];
}

export interface ConsultResponse {
  id: number;
  code: number;
  message: string;
  status: string;
}

export const getConsultsMetaData = async (
  facilityUserId: number
): Promise<ConsultsMetaData> => {
  const response = await httpRequest<ConsultsMetaData>({
    method: 'GET',
    url: `/facility/consult/${facilityUserId}/metadata`,
  });
  return response;
};

export const getIntakeFormForConsult = async (
  userId: string,
  facilityId: number,
  consultId?: number
): Promise<DynamicForm> => {
  let url = `/integration/consult/intake-form?facilityId=${facilityId}`;

  if (consultId !== undefined) {
    url += `&consultId=${consultId}`;
  }
  const response = await httpRequest<DynamicForm>({
    method: 'GET',
    url,
    headers: { userId, portalType: PORTAL_TYPES.FACILITY },
    apiBaseType: 'INTEGRATION',
  });

  return response;
};

export const saveConsultsForm = async (
  userId: string,
  payload: ConsultForm
) => {
  const response = await httpRequest<ConsultResponse>({
    method: 'POST',
    url: `/facility/consult`,
    data: payload,
    withCredentials: true,
    headers: { userId },
  });
  return response;
};

export const getCancelConsultMetadata =
  async (): Promise<CancelConsultMetadataResponse> => {
    const response = await httpRequest<CancelConsultMetadataResponse>({
      url: `integration/consult/cancel/metadata`,
      method: 'GET',
      headers: {
        portalType: PORTAL_TYPES.FACILITY,
      },
      apiBaseType: 'INTEGRATION',
    });
    return response;
  };

export const getConsultDetailsById = async (
  userId: string,
  consultId: number
): Promise<ConsultForm> => {
  const response = await httpRequest<ConsultForm>({
    method: 'GET',
    url: `/facility/case-manager/consult?consultId=${consultId}`,
    headers: { userId },
  });
  return response;
};

export const saveOrUpdateConsultsForm = async (
  userId: string,
  action: string,
  payload: ConsultForm
) => {
  const apiAction = action === 'Save' ? 'SAVE' : 'UPDATE';
  const response = await httpRequest<ConsultResponse>({
    method: 'PUT',
    url: `/facility/case-manager/consult?action=${apiAction}`,
    headers: { userId },
    data: payload,
  });
  return response;
};

export const saveCancelConsult = async (
  userId: string,
  consultId: number,
  payload: CancelReasonFacilityParams
) => {
  const response = await httpRequest<CancelReasonFacilityParams>({
    method: 'PUT',
    url: `/integration/consult/${consultId}/cancel`,
    headers: { userId, portalType: PORTAL_TYPES.FACILITY },
    data: payload,
    apiBaseType: 'INTEGRATION',
  });
  return response;
};
